import { useCallback, useState } from 'react'
import { CanonicalLoad } from '@numeo/types-core'
import { FactoringProvider } from 'services/factoring'
import { FactoringApprovalChip } from 'features/load-hub/components/expanded-row/FactoringApprovalChip'
import { BOX_TRUCK_FACTORING_PROVIDERS, useBoxTruckFactoringProviderStore } from '../hooks/useBoxTruckFactoringProvider'
import { useProviderLinkState } from '../hooks/useProviderLinkState'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { brokerCacheKey, isRevealed, recordRevealed, revealKey } from '../utils/revealedRatings'

/** The broker MC reduced to digits (same rule as @numeo_private/utils `mcNumberDigitsOnly` — not imported:
 *  admin has no dependency on that package). Empty → no MC on this load (Sylectus), so the lookup falls
 *  through to the broker company name, which RTS, TAFS and eCapital can resolve through their debtor directories. */
const brokerMc = (load: CanonicalLoad): string | null => {
    const digits = (load.brokerMc ?? '').replace(/\D/g, '')
    return digits || null
}

/**
 * Box-truck broker rating using the shared Load Hub chip. Selection lives in the box-truck-scoped
 * persisted store: switching between TAFS, RTS and eCapital sticks across loads and reloads without overwriting
 * the app-wide provider used by Load Hub and AI Hub.
 *
 * The rating is NOT fetched when the drawer opens. Operators scan the board by opening and closing
 * loads in seconds, so a fetch-on-open spent an RTS/TAFS portal lookup — rate-limited to 30/min per
 * project and guarded by a circuit breaker — on loads nobody read. It now waits for "See Rating".
 *
 * A provider the operator has never linked is the exception: it shows its Connect prompt immediately,
 * because there is no lookup to save there (the chip stops at the auth check) and hiding the prompt
 * behind a button would make a missing connection look like a rating they simply hadn't opened yet.
 * That exception needs the status answer to be KNOWN — an unanswered or failed status defers like any
 * other uncertainty, since the alternative is failing open on exactly the provider that is linked.
 *
 * A broker they HAVE revealed stays revealed for ten minutes (see `utils/revealedRatings`), so reopening
 * a load doesn't hide an answer they already asked for — a grade or a definitive "no rating", both being
 * answers about the broker. Only that fact is remembered, never the rating itself: the value comes back
 * off the caches the normal fetch already goes through.
 */
const BoxTruckFactoringBadgeForLoad = ({ load }: { load: CanonicalLoad }) => {
    const provider = useBoxTruckFactoringProviderStore((s) => s.provider)
    const setProvider = useBoxTruckFactoringProviderStore((s) => s.setProvider)
    const linkState = useProviderLinkState(provider)

    const mc = brokerMc(load)
    const company = load.brokerCompanyName?.trim() || null
    const brokerKey = brokerCacheKey(mc, company)
    const cacheKey = brokerKey ? revealKey(provider, brokerKey) : null

    // Seeded from the shared memory, not only from a press in this mount.
    //
    // Reading `isRevealed` live on every render meant a rating already on screen could be taken away:
    // open a load 9.5 minutes into the window, the chip fetches and shows the grade, and the next
    // re-render after the entry lapses — a bid arriving over the socket is enough, this is not memoised —
    // flips `deferred` back on, nulls the hook's keys and replaces the grade with See Rating. Latching the
    // answer for this mount is what the comment always claimed: an entry ageing out while the drawer is
    // open cannot re-hide what is on screen.
    //
    // Derived during render for the same reason useProviderLinkState pairs its answer with a provider:
    // re-seeding in an effect left one commit in which a broker revealed under TAFS still read as revealed
    // after the operator picked RTS, which is the case this feature is most explicit about not wanting
    // ("wanting one is not consent to spend the other").
    const [revealedFor, setRevealedFor] = useState<string | null>(() => (cacheKey && isRevealed(cacheKey) ? cacheKey : null))
    // React's documented "adjust state when a prop changes" — a re-render started immediately, before
    // any DOM work, rather than an effect that would leave one commit showing the previous key's answer.
    // The previous key is STATE, not a ref: a ref would already have moved if React discarded the
    // render, while the `setRevealedFor` beside it was thrown away, and a broker the operator had
    // unlocked would silently go back behind See Rating.
    const [seenCacheKey, setSeenCacheKey] = useState(cacheKey)
    if (seenCacheKey !== cacheKey) {
        setSeenCacheKey(cacheKey)
        // A key change is a different broker or a different provider — this mount's latch says nothing
        // about it, so fall back to what the shared memory knows.
        setRevealedFor(cacheKey && isRevealed(cacheKey) ? cacheKey : null)
    }
    const revealed = revealedFor === cacheKey && cacheKey != null

    // A completed lookup earns the ten minutes — a grade, or a definitive "no rating". Both are answers
    // about the broker that the operator asked for and saw, and re-hiding either behind the button on the
    // next load from the same brokerage is the thing this memory exists to stop. Auth failures and
    // unavailable providers never reach here: they say nothing about the broker, so they stay re-askable.
    const rememberLookup = useCallback(() => {
        if (cacheKey) recordRevealed(cacheKey)
    }, [cacheKey])

    if (!mc && !company) return null

    const handleProviderChange = (nextProvider: FactoringProvider) => {
        trackBoxTruckUiInteraction('factoring_provider_changed', nextProvider)
        setProvider(nextProvider)
    }

    // The press only opens THIS chip. It deliberately does not write the shared memory: a press that
    // resolves to an auth failure would otherwise mark the broker revealed for ten minutes, and the
    // memory is keyed by broker — so every other load from that brokerage would open un-deferred and
    // re-attempt the failing lookup with no press at all, spending the budget against a provider that
    // is already rejecting.
    const handleReveal = () => {
        // The link state rides along because the button is offered before the answer is known: the badge
        // remounts per load and starts at 'unknown', which defers, so an unlinked provider shows See
        // Rating for at least a frame and resolves to Connect. Those presses produced no lookup, and this
        // event is the measure of how often a rating is WANTED per load opened — recording the state the
        // press was made under keeps that number readable instead of silently inflating it. Bounded:
        // three providers by three states.
        trackBoxTruckUiInteraction('factoring_rating_revealed', `${provider}:${linkState}`)
        setRevealedFor(cacheKey)
    }

    return (
        <div className="mt-2">
            <FactoringApprovalChip
                mcNumber={mc}
                companyName={company}
                forceProvider={provider}
                onProviderChange={handleProviderChange}
                providers={BOX_TRUCK_FACTORING_PROVIDERS}
                isFactorable={null}
                showScore={provider === FactoringProvider.TAFS}
                onGradeFetched={rememberLookup}
                onLookupNotFound={rememberLookup}
                {...(linkState === 'unlinked' || revealed ? { deferred: false as const } : { deferred: true as const, onReveal: handleReveal })}
            />
        </div>
    )
}

/** Key the badge by load identity so switching rows in an already-open drawer remounts the shared chip
 * with a clean fetch state for the new broker instead of carrying the previous one's over. That remount
 * is also what resets "See Rating", so a load whose rating was never opened costs nothing to skim past.
 * A broker already revealed is unaffected: the remount re-reads the reveal memory and opens revealed.
 * The provider survives too — it lives in the store, not in component state. */
export const BoxTruckFactoringBadge = ({ load }: { load: CanonicalLoad }) => (
    <BoxTruckFactoringBadgeForLoad key={load.externalId || String(load.id)} load={load} />
)
